import React, { useState, useEffect } from "react";
import {
  Grid,
  Card,
  Input,
  Button,
  Modal,
  Header,
  Form,
  TextArea,
  Dropdown,
  Popup,
  Table,
  Message,
  Menu,
  Image,
} from "semantic-ui-react";
import {
  Table as MuiTable,
  TableBody as MuiTableBody,
  TableCell as MuiTableCell,
  TableContainer as MuiTableContainer,
  TableHead as MuiTableHead,
  TableRow as MuiTableRow,
  Paper,
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import moment from "moment";
import { jwtDecode } from "jwt-decode";
import {
  getGroups,
  createGroup,
  updateGroup,
  deleteGroup,
  getGroupUsers,
  getUsersList,
  GetGroupsSuccess,
  CreateGroupSuccess,
  UpdateGroupSuccess,
  DeleteGroupSuccess,
} from "../../actions/dataConsoleAction";
import "../../styles/GroupManagement.css";
import "../../styles/DataconsoleNavbar.css";
import { borderRadius } from "@mui/system";
import SearchableMultiSelectDropdown from "../../uiComponents/SearchableMultiSelectDropdown";

const GroupManagement = ({ showToaster, userType, onSendGroupsCount, onSearchDataReceived, onCreateGroupClick }) => {
  const dispatch = useDispatch();

  // State for groups
  const [groups, setGroups] = useState([]);
  const [filteredGroups, setFilteredGroups] = useState([]);
  const [loading, setLoading] = useState(false);

  // Modal states
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [groupUsersModalOpen, setGroupUsersModalOpen] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [userSearchTerm, setUserSearchTerm] = useState("");

  // Form states
  const [groupName, setGroupName] = useState("");
  const [groupDescription, setGroupDescription] = useState("");
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [availableUsers, setAvailableUsers] = useState([]);

  // Group users modal states
  const [groupUsers, setGroupUsers] = useState([]);
  const [selectedGroupUsers, setSelectedGroupUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [loadingGroupUsers, setLoadingGroupUsers] = useState(false);
  const [loadingEditUsers, setLoadingEditUsers] = useState(false);
  const [loadingGroupId, setLoadingGroupId] = useState(null);

  // Form validation
  const [nameError, setNameError] = useState(false);
  const [descriptionError, setDescriptionError] = useState(false);

  // Loading states for API calls
  const [isCreating, setIsCreating] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  // Track original values for edit modal to detect changes
  const [originalGroupName, setOriginalGroupName] = useState("");
  const [originalGroupDescription, setOriginalGroupDescription] = useState("");
  const [originalSelectedUsers, setOriginalSelectedUsers] = useState([]);

  // Track original values for manage group users modal
  const [originalGroupUsers, setOriginalGroupUsers] = useState([]);

  // Redux selectors
  const { groupsData, groupsDataStatus } = useSelector(
    (state) => state.groupsReducer || {}
  );

  const { createGroupResponse, createGroupStatus } = useSelector(
    (state) => state.createGroupReducer || {}
  );

  const { updateGroupResponse, updateGroupStatus } = useSelector(
    (state) => state.updateGroupReducer || {}
  );

  const { deleteGroupResponse, deleteGroupStatus } = useSelector(
    (state) => state.deleteGroupReducer || {}
  );

  const { allUsersData: usersData, allUsersDataStatus: usersDataStatus } = useSelector(
    (state) => state.allUsersList || {}
  );

  const { groupUsersData, groupUsersDataStatus, loading: groupUsersApiLoading } = useSelector(
    (state) => state.groupUsersReducer || {}
  );

  // Get current user info
  const getCurrentUser = () => {
    try {
      const token = localStorage.getItem("token");
      if (token) {
        const decoded = jwtDecode(token);
        return {
          id: decoded.oid || decoded.sub || decoded.userId || decoded.id || "",
          name: decoded.name || decoded.given_name || decoded.email || decoded.emails?.[0] || decoded.preferred_username || "Unknown User",
          organization: decoded.extension_Organization || decoded.org || "default"
        };
      }
    } catch (error) {
      console.error("Error decoding token:", error);
    }
    return { id: "", name: "Unknown User", organization: "default" };
  };

  // Load data on component mount
  useEffect(() => {
    loadGroups();
    loadUsers();
  }, []);

  // Expose search function to parent via callback
  useEffect(() => {
    if (onSearchDataReceived) {
      onSearchDataReceived(setSearchTerm);
    }
  }, [onSearchDataReceived]);

  // Expose create function to parent via callback
  useEffect(() => {
    if (onCreateGroupClick) {
      onCreateGroupClick(openCreateModal);
    }
  }, [onCreateGroupClick]);

  // Handle Redux state changes
  useEffect(() => {
    if (groupsDataStatus === 200 && groupsData) {
      // Handle different possible data structures
      let groupsArray = [];
      if (Array.isArray(groupsData)) {
        groupsArray = groupsData;
      } else if (groupsData.data && Array.isArray(groupsData.data)) {
        groupsArray = groupsData.data;
      } else if (groupsData.success && groupsData.data && Array.isArray(groupsData.data)) {
        groupsArray = groupsData.data;
      } else {
        console.warn("Unexpected groups data structure:", groupsData);
        groupsArray = [];
      }

      setGroups(groupsArray);
      setFilteredGroups(groupsArray);
      setLoading(false);
      // Send group count to navbar
      if (onSendGroupsCount) {
        onSendGroupsCount(groupsArray.length);
      }
    } else if (groupsDataStatus && groupsDataStatus !== 200) {
      // Handle error cases
      setLoading(false);
      showToaster("Failed to load groups. Please try again.", "error");
    }
  }, [groupsData, groupsDataStatus]);

  useEffect(() => {
    if (createGroupStatus === 200 && createGroupResponse) {
      // showToaster("Group created successfully", "success");
      setCreateModalOpen(false);
      resetForm();
      loadGroups();
      setIsCreating(false);
      CreateGroupSuccess([]);
    } else if (createGroupStatus && createGroupStatus !== 200 && isCreating) {
      showToaster("Failed to create group", "error");
      setIsCreating(false);
    }
  }, [createGroupResponse, createGroupStatus]);

  useEffect(() => {
    if (updateGroupStatus === 200 && updateGroupResponse) {
      // showToaster("Group updated successfully", "success");
      setEditModalOpen(false);
      resetForm();
      loadGroups();
      setIsUpdating(false);
      setGroupUsersModalOpen(false);
      UpdateGroupSuccess([]);
    } else if (updateGroupStatus && updateGroupStatus !== 200 && isUpdating) {
      showToaster("Failed to update group", "error");
      setIsUpdating(false);
    }
  }, [updateGroupResponse, updateGroupStatus]);

  useEffect(() => {
    if (deleteGroupStatus === 200 && deleteGroupResponse) {
      // showToaster("Group deleted successfully", "success");
      setDeleteModalOpen(false);
      setIsDeleting(false);
      loadGroups();
      DeleteGroupSuccess([]);
    } else if (deleteGroupStatus && deleteGroupStatus !== 200 && isDeleting) {
      showToaster("Failed to delete group", "error");
      setIsDeleting(false);
    }
  }, [deleteGroupResponse, deleteGroupStatus]);

  useEffect(() => {
    if (usersDataStatus === 200 && usersData) {
      console.log("Users data received:", usersData);

      // Handle different possible data structures from the API
      let usersArray = [];
      if (Array.isArray(usersData)) {
        usersArray = usersData;
      } else if (usersData.data && Array.isArray(usersData.data)) {
        usersArray = usersData.data;
      } else if (usersData.users && Array.isArray(usersData.users)) {
        usersArray = usersData.users;
      } else {
        console.warn("Unexpected users data structure:", usersData);
        usersArray = [];
      }

      const userOptions = usersArray.map(user => ({
        key: user.id || user.oid || user.objectId,
        value: user.id || user.oid || user.objectId,
        text: `${user.displayName || user.name || user.givenName || 'Unknown User'} (${user.mail || user.email || user.userPrincipalName || 'No email'})`,
      }));

      console.log("Processed user options:", userOptions);
      setAvailableUsers(userOptions);
    } else if (usersDataStatus && usersDataStatus !== 200) {
      console.error("Failed to load users. Status:", usersDataStatus);
    }
  }, [usersData, usersDataStatus]);

  // Handle group users data response
  useEffect(() => {
    // Only process when API has finished loading AND we're waiting for data
    if (groupUsersApiLoading) {
      return; // API is still loading, wait
    }

    // Don't process if we're not expecting data
    if (!loadingEditUsers && !loadingGroupUsers) {
      return;
    }

    if (groupUsersDataStatus === 200 && groupUsersData) {
      // Handle different possible data structures from the API
      let groupUsersArray = [];
      if (Array.isArray(groupUsersData)) {
        groupUsersArray = groupUsersData;
      } else if (groupUsersData.data && Array.isArray(groupUsersData.data)) {
        groupUsersArray = groupUsersData.data;
      } else {
        console.warn("Unexpected group users data structure:", groupUsersData);
        groupUsersArray = [];
      }

      // Extract user IDs from group members
      const currentUsers = groupUsersArray.map(member => member.userId || member.id || member);
      setSelectedGroupUsers(currentUsers);

      // Merge external users (who have userDisplayName/userEmail but are not in availableUsers)
      // This ensures users from different organizations (e.g., EXT: users) are visible
      setAvailableUsers(prevUsers => {
        const existingUserIds = new Set(prevUsers.map(u => u.value));
        const newExternalUsers = groupUsersArray
          .filter(member => {
            const userId = member.userId || member.id;
            return userId && !existingUserIds.has(userId) && (member.userDisplayName || member.userEmail);
          })
          .map(member => ({
            key: member.userId || member.id,
            value: member.userId || member.id,
            text: `${member.userDisplayName || 'Unknown User'} (${member.userEmail || 'No email'})`,
          }));

        if (newExternalUsers.length > 0) {
          return [...prevUsers, ...newExternalUsers];
        }
        return prevUsers;
      });

      // If edit modal is open and loading, populate users and stop loading
      if (editModalOpen && loadingEditUsers) {
        setSelectedUsers(currentUsers);
        setOriginalSelectedUsers(currentUsers); // Store original users for comparison
        setLoadingEditUsers(false);
        setLoadingGroupId(null);
      }

      // If group users modal is open, stop loading
      if (groupUsersModalOpen && loadingGroupUsers) {
        setOriginalGroupUsers(currentUsers); // Store original users for comparison
        setLoadingGroupUsers(false);
      }
    } else if (groupUsersDataStatus && groupUsersDataStatus !== 200) {
      console.error("Failed to load group users. Status:", groupUsersDataStatus);
      showToaster("Failed to load group members", "error");
      setLoadingGroupUsers(false);
      setLoadingEditUsers(false);
      setLoadingGroupId(null);
    }
  }, [groupUsersData, groupUsersDataStatus, groupUsersApiLoading, editModalOpen, loadingEditUsers, groupUsersModalOpen, loadingGroupUsers]);

  // Search functionality
  useEffect(() => {
    // Ensure groups is an array before filtering
    const groupsArray = Array.isArray(groups) ? groups : [];

    if (searchTerm === "") {
      setFilteredGroups(groupsArray);
    } else {
      const filtered = groupsArray.filter(group => {
        const createdByName = getUserNameById(group.createdBy);

        return group.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          group.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          createdByName.toLowerCase().includes(searchTerm.toLowerCase());
      });
      setFilteredGroups(filtered);
    }
  }, [searchTerm, groups, availableUsers]);

  // User search functionality for group users modal
  useEffect(() => {
    let usersToFilter = availableUsers;

    // Apply search filter first
    if (userSearchTerm !== "") {
      usersToFilter = usersToFilter.filter(user =>
        user.text?.toLowerCase().includes(userSearchTerm.toLowerCase())
      );
    }

    // Sort users: selected users first (alphabetically), then unselected users (alphabetically)
    const sortedUsers = [...usersToFilter].sort((a, b) => {
      const aSelected = selectedGroupUsers.includes(a.value);
      const bSelected = selectedGroupUsers.includes(b.value);

      // If one is selected and the other isn't, selected comes first
      if (aSelected && !bSelected) return -1;
      if (!aSelected && bSelected) return 1;

      // Both selected or both unselected - sort alphabetically by name
      const aName = a.text.split(' (')[0].toLowerCase();
      const bName = b.text.split(' (')[0].toLowerCase();
      return aName.localeCompare(bName);
    });

    setFilteredUsers(sortedUsers);
  }, [userSearchTerm, availableUsers, selectedGroupUsers]);

  const loadGroups = () => {
    setLoading(true);
    dispatch(getGroups());
  };

  const loadUsers = () => {
    dispatch(getUsersList());
  };

  const resetForm = () => {
    setGroupName("");
    setGroupDescription("");
    setSelectedUsers([]);
    setNameError(false);
    setDescriptionError(false);
    setSelectedGroup(null);
  };

  const validateForm = () => {
    let isValid = true;

    if (!groupName.trim()) {
      setNameError(true);
      isValid = false;
    } else {
      setNameError(false);
    }

    if (!groupDescription.trim()) {
      setDescriptionError(true);
      isValid = false;
    } else {
      setDescriptionError(false);
    }

    return isValid;
  };

  // Check if there are any changes in the edit form
  const hasEditChanges = () => {
    // Check if name or description changed
    const nameChanged = groupName.trim() !== originalGroupName.trim();
    const descriptionChanged = groupDescription.trim() !== originalGroupDescription.trim();

    // Check if users changed (compare arrays)
    const usersChanged =
      selectedUsers.length !== originalSelectedUsers.length ||
      !selectedUsers.every(userId => originalSelectedUsers.includes(userId)) ||
      !originalSelectedUsers.every(userId => selectedUsers.includes(userId));

    return nameChanged || descriptionChanged || usersChanged;
  };

  // Check if there are any changes in the manage group users modal
  const hasGroupUsersChanges = () => {
    // Compare selected group users with original group users
    return (
      selectedGroupUsers.length !== originalGroupUsers.length ||
      !selectedGroupUsers.every(userId => originalGroupUsers.includes(userId)) ||
      !originalGroupUsers.every(userId => selectedGroupUsers.includes(userId))
    );
  };

  const handleCreateGroup = () => {
    if (!validateForm()) {
      showToaster("Please fill in all required fields", "error");
      return;
    }

    setIsCreating(true);

    const currentUser = getCurrentUser();

    // Build members array with user display information
    const members = selectedUsers.map(userId => {
      const userOption = availableUsers.find(u => u.value === userId);
      if (userOption) {
        // Extract displayName and email from the text: "DisplayName (email)"
        const text = userOption.text;
        const displayName = text.split(' (')[0];
        const email = text.match(/\(([^)]+)\)/)?.[1] || '';

        return {
          userId: userId,
          userDisplayName: displayName,
          userEmail: email
        };
      }
      // Fallback if user not found in availableUsers
      return {
        userId: userId,
        userDisplayName: '',
        userEmail: ''
      };
    });

    const payload = {
      name: groupName.trim(),
      description: groupDescription.trim(),
      organizationId: currentUser.organization,
      createdBy: currentUser.id,
      createdByDisplayName: currentUser.name,
      memberIds: selectedUsers,
      members: members, // Add members with display info
    };

    dispatch(createGroup(payload));
    showToaster("Group created successfully", "success");

  };

  const handleEditGroup = () => {
    if (!validateForm()) {
      showToaster("Please fill in all required fields", "error");
      return;
    }

    setIsUpdating(true);

    const payload = {
      name: groupName.trim(),
      description: groupDescription.trim(),
      memberIds: selectedUsers,
    };

    dispatch(updateGroup(selectedGroup.id, payload));
    showToaster("Group updated successfully", "success");

  };

  const handleDeleteGroup = () => {
    if (selectedGroup) {
      setIsDeleting(true);
      dispatch(deleteGroup(selectedGroup.id));
      showToaster("Group deleted successfully", "success");

    }
  };

  const openCreateModal = () => {
    resetForm();
    setCreateModalOpen(true);
  };

  const openEditModal = async (group) => {
    setSelectedGroup(group);
    setGroupName(group.name || "");
    setGroupDescription(group.description || "");
    setSelectedUsers([]);

    // Store original values for comparison
    setOriginalGroupName(group.name || "");
    setOriginalGroupDescription(group.description || "");
    // originalSelectedUsers will be set when data loads

    // Start loading FIRST and track which group we're loading
    setLoadingEditUsers(true);
    setLoadingGroupId(group.id);

    // Then open modal
    setEditModalOpen(true);

    // Fetch group users
    dispatch(getGroupUsers(group.id));
  };

  const openDeleteModal = (group) => {
    setSelectedGroup(group);
    setDeleteModalOpen(true);
  };

  const openGroupUsersModal = (group) => {
    setSelectedGroup(group);
    setUserSearchTerm("");
    setLoadingGroupUsers(true);
    setSelectedGroupUsers([]); // Clear previous selection until data loads
    setOriginalGroupUsers([]); // Reset original users

    // Fetch current group users using Redux action since they're stored separately now
    dispatch(getGroupUsers(group.id));

    // Set all available users for the modal
    setFilteredUsers(availableUsers);
    setGroupUsersModalOpen(true);
  };

  const closeGroupUsersModal = () => {
    setGroupUsersModalOpen(false);
    setSelectedGroup(null);
    setUserSearchTerm("");
    setSelectedGroupUsers([]);
    setOriginalGroupUsers([]);
    setFilteredUsers([]);
    setLoadingGroupUsers(false);
  };

  const handleUserToggle = (userId) => {
    setSelectedGroupUsers(prev => {
      if (prev.includes(userId)) {
        return prev.filter(id => id !== userId);
      } else {
        return [...prev, userId];
      }
    });
  };

  const handleSaveGroupUsers = () => {
    if (!selectedGroup) return;

    setIsUpdating(true);

    // Build members array with user display information
    const members = selectedGroupUsers.map(userId => {
      const userOption = availableUsers.find(u => u.value === userId);
      if (userOption) {
        // Extract displayName and email from the text: "DisplayName (email)"
        const text = userOption.text;
        const displayName = text.split(' (')[0];
        const email = text.match(/\(([^)]+)\)/)?.[1] || '';

        return {
          userId: userId,
          userDisplayName: displayName,
          userEmail: email
        };
      }
      // Fallback if user not found in availableUsers
      return {
        userId: userId,
        userDisplayName: '',
        userEmail: ''
      };
    });

    const payload = {
      name: selectedGroup.name,
      description: selectedGroup.description,
      memberIds: selectedGroupUsers, // Keep for backward compatibility
      members: members, // New field with display information
    };

    dispatch(updateGroup(selectedGroup.id, payload));
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Unknown';
    try {
      return moment(dateString).format("MMM DD, YYYY");
    } catch (error) {
      console.warn("Invalid date format:", dateString);
      return 'Invalid Date';
    }
  };

  const getMemberCount = (group) => {
    // Use the cached memberCount from the backend instead of filtering members array
    return group?.memberCount || 0;
  };

  // Safe render utility to handle object/string display
  const safeRender = (value, fallback = 'Unknown') => {
    if (typeof value === 'string') return value;
    if (typeof value === 'object' && value !== null) {
      if (value.displayName) return value.displayName;
      if (value.name) return value.name;
      if (value.id) return value.id;
    }
    return fallback;
  };

  // Get user name from user ID
  const getUserNameById = (createdByValue) => {
    if (!createdByValue) return 'Unknown User';

    let userId = null;

    // If createdBy is an object, extract the user ID and check for displayName
    if (typeof createdByValue === 'object' && createdByValue !== null) {
      // If displayName exists and is not empty, use it
      if (createdByValue.displayName && createdByValue.displayName.trim()) {
        return createdByValue.displayName;
      }
      if (createdByValue.name && createdByValue.name.trim()) {
        return createdByValue.name;
      }
      // Extract the ID for lookup
      userId = createdByValue.id || createdByValue.userId;
    } else {
      // If it's a string, use it as the user ID
      userId = String(createdByValue);
    }

    if (!userId) return 'Unknown User';

    // First check if the userId matches the current user
    const currentUser = getCurrentUser();
    if (currentUser.id === userId) {
      return currentUser.name;
    }

    // Then check in availableUsers
    const user = availableUsers.find(u => u.value === userId);
    if (user) {
      // Extract just the name part (before the email in parentheses)
      return user.text.split(' (')[0];
    }

    // If user not found, return Unknown User (don't show the UUID)
    return 'Unknown User';
  };

  // // Check user permissions
  // const hasCreatePermission = () => {
  //   return ["Admin", "OrgAdmin", "DatasetAdmin"].includes(userType);
  // };

  // const hasEditPermission = () => {
  //   return ["Admin", "OrgAdmin", "DatasetAdmin"].includes(userType);
  // };

  // const hasDeletePermission = () => {
  //   return ["Admin", "OrgAdmin"].includes(userType);
  // };

  return (
    <div className="group-management-container">
      <div className={`groups-table-container ${(loading || !Array.isArray(filteredGroups) || filteredGroups.length === 0) ? 'empty-state' : ''}`}>
          <Table celled striped>
            <Table.Header>
          <Table.Row>
            <Table.HeaderCell >Group Name</Table.HeaderCell>
            <Table.HeaderCell >Description</Table.HeaderCell>
            <Table.HeaderCell >Members</Table.HeaderCell>
            <Table.HeaderCell >Created Date</Table.HeaderCell>
            <Table.HeaderCell >Created By</Table.HeaderCell>
            <Table.HeaderCell style={{ marginRight: 0 }}>Actions</Table.HeaderCell>
          </Table.Row>
            </Table.Header>
            <Table.Body>
          {loading ? (
            <div style={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              zIndex: 10,
              gap: '0'
            }}>
              <div style={{ textAlign: 'center' }}>
                <div className="ui active inline loader" style={{ marginBottom: '12px' }}></div>
                <p style={{ margin: '0', fontSize: '16px', color: '#6b7280' }}>Loading groups...</p>
              </div>
            </div>
          ) : (!Array.isArray(filteredGroups) || filteredGroups.length === 0) ? (
            <div style={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              zIndex: 10
            }}>
              <div style={{ textAlign: 'center', maxWidth: '400px' }}>
                <h3 style={{ margin: '0 0 12px 0', fontSize: '20px', fontWeight: '600', color: '#1f2937' }}>
                  No groups found
                </h3>
                <p style={{ margin: '0', fontSize: '16px', color: '#6b7280', lineHeight: '1.5' }}>
                  {searchTerm
                  ? "Try adjusting your search criteria or create a new group."
                  : "Get started by creating your first group."
                  }
                </p>
              </div>
            </div>
          ) : (
            Array.isArray(filteredGroups) && filteredGroups.map((group) => (
              <Table.Row key={group.id}>
            <Table.Cell>
              <strong
                style={{ cursor: 'pointer', color: '#0053ed', textDecoration: 'underline' }}
                onClick={() => openGroupUsersModal(group)}
              >
                {safeRender(group.name, 'Untitled Group')}
              </strong>
            </Table.Cell>
            <Table.Cell>
              <div className="group-description">
                {safeRender(group.description, 'No description')}
              </div>
            </Table.Cell>
            <Table.Cell>
              {getMemberCount(group)} members
            </Table.Cell>
            <Table.Cell>
              {formatDate(group.createdDate)}
            </Table.Cell>
            <Table.Cell>
              {getUserNameById(group.createdBy)}
            </Table.Cell>
            <Table.Cell className="actions-cell">
              <div className="actions-container">
                <div className="user-card-icons grey-icons">
                <div>
                  <Popup
                content="Edit group"
                inverted
                position="top center"
                style={{ borderRadius: "15px" }}
                trigger={
                  <img
                    className="tableButton"
                    src="/images/Edit.svg"
                    alt="Edit"
                    onClick={() => openEditModal(group)}
                    style={{ cursor: "pointer" }}
                  />
                }
                  />
                </div>
                <div>
                  <Popup
                content="Delete group"
                inverted
                position="top center"
                style={{ borderRadius: "15px" }}
                trigger={
                  <img
                    className="tableButton"
                    src="/images/grey-delete.svg"
                    alt="Delete"
                    onClick={() => openDeleteModal(group)}
                    style={{ cursor: "pointer" }}
                  />
                }
                  />
                </div>
                </div>
                <div className="user-card-icons color-icons">
                <div>
                  <Popup
                content="Edit group"
                inverted
                position="top center"
                style={{ borderRadius: "15px" }}
                trigger={
                  <img
                    className="tableButton"
                    src="/images/blue-pen.svg"
                    alt="Edit"
                    onClick={() => openEditModal(group)}
                    style={{ cursor: "pointer" }}
                  />
                }
                  />
                </div>
                <div>
                  <Popup
                content="Delete group"
                inverted
                position="top center"
                style={{ borderRadius: "15px" }}
                trigger={
                  <img
                    className="tableButton"
                    src="/images/red-delete.svg"
                    alt="Delete"
                    onClick={() => openDeleteModal(group)}
                    style={{ cursor: "pointer" }}
                  />
                }
                  />
                </div>
                </div>
              </div>
            </Table.Cell>
              </Table.Row>
            ))
          )}
            </Table.Body>
          </Table>
        </div>

        {/* Create Group Modal */}
      <Modal
        open={createModalOpen}
        onClose={() => setCreateModalOpen(false)}
        size="tiny"
        className="add-document-model"
      >
        <Modal.Content>
          <Header as="h3">Create New Group</Header>
          <label>Group Name</label>
          <br />
          <Input
            type="text"
            value={groupName}
            onChange={(e) => setGroupName(e.target.value)}
            placeholder="Enter group name"
            className="add-datset-input"
          />
          <br />
          {nameError && (
            <span style={{ color: "red" }}>
              &nbsp;Group name is required.
            </span>
          )}
          <br />
          <label>Description</label>
          <br />
          <div style={{ marginTop: '8px' }}>
            <textarea
              placeholder="Enter group description"
              value={groupDescription}
              onChange={(e) => setGroupDescription(e.target.value)}
              rows={3}
              className="group-description-textarea"
              style={{
                width: '100%',
                padding: '8px 12px',
                border: '1px solid #d1d5db',
                borderRadius: '16px',
                fontSize: '14px',
                fontFamily: 'inherit',
                resize: 'vertical'
              }}
            />
          </div>
          {descriptionError && (
            <span style={{ color: "red" }}>
              &nbsp;Description is required.
            </span>
          )}
          <br />
          <label>Add Members (Optional)</label>
          <SearchableMultiSelectDropdown
            options={availableUsers}
            selectedValues={selectedUsers}
            onChange={setSelectedUsers}
            placeholder="Search and select users to add"
            loading={usersDataStatus !== 200}
          />
        </Modal.Content>
        <Modal.Actions>
          <button
            className="cancel-btn"
            onClick={() => setCreateModalOpen(false)}
          >
            Cancel
          </button>
          <button
            disabled={!groupName.trim() || !groupDescription.trim() || isCreating}
            className="add-btn"
            onClick={handleCreateGroup}
          >
            {isCreating ? 'Creating...' : 'Create'}
          </button>
        </Modal.Actions>
      </Modal>

      {/* Edit Group Modal */}
      <Modal
        open={editModalOpen}
        onClose={() => setEditModalOpen(false)}
        size="tiny"
        className="add-document-model"
      >
        <Modal.Content>
          <Header as="h3">Edit Group</Header>
          <label>Group Name</label>
          <br />
          <Input
            type="text"
            value={groupName}
            onChange={(e) => setGroupName(e.target.value)}
            placeholder="Enter group name"
            className="add-datset-input"
          />
          <br />
          {nameError && (
            <span style={{ color: "red" }}>
              &nbsp;Group name is required.
            </span>
          )}
          <br />
          <label>Description</label>
          <br />
          <div style={{ marginTop: '8px' }}>
            <textarea
              placeholder="Enter group description"
              value={groupDescription}
              onChange={(e) => setGroupDescription(e.target.value)}
              rows={3}
              className="group-description-textarea"
              style={{
                width: '100%',
                padding: '8px 12px',
                border: '1px solid #d1d5db',
                borderRadius: '16px',
                fontSize: '14px',
                fontFamily: 'inherit',
                resize: 'vertical'
              }}
            />
          </div>
          {descriptionError && (
            <span style={{ color: "red" }}>
              &nbsp;Description is required.
            </span>
          )}
          <br />
          <label>Add Members</label>
          <br />
          {loadingEditUsers ? (
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '20px',
              marginTop: '8px'
            }}>
              <div className="ui active inline loader" style={{ marginRight: '12px' }}></div>
              <span style={{ color: '#6b7280', fontSize: '14px' }}>Loading members...</span>
            </div>
          ) : (
            <SearchableMultiSelectDropdown
              options={availableUsers}
              selectedValues={selectedUsers}
              onChange={setSelectedUsers}
              placeholder="Search and select users to add"
              loading={usersDataStatus !== 200}
            />
          )}
        </Modal.Content>
        <Modal.Actions>
          <button
            className="cancel-btn"
            onClick={() => setEditModalOpen(false)}
          >
            Cancel
          </button>
          <button
            disabled={!groupName.trim() || !groupDescription.trim() || isUpdating || !hasEditChanges()}
            className="add-btn"
            onClick={handleEditGroup}
          >
            {isUpdating ? 'Updating...' : 'Update'}
          </button>
        </Modal.Actions>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        open={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        size="tiny"
        className="logout-modal"
      >
        <Modal.Content>
          <Header as="h3">Delete Group</Header>
          <p style={{ textAlign: "center" }}>
            Are you sure you want to delete the group "{selectedGroup?.name}"?
            This action cannot be undone.
          </p>
        </Modal.Content>
        <Modal.Actions>
          <Button onClick={() => setDeleteModalOpen(false)} disabled={isDeleting}>Cancel</Button>
          <Button
            negative
            onClick={handleDeleteGroup}
            disabled={isDeleting}
          >
            {isDeleting ? 'Deleting...' : 'Delete'}
          </Button>
        </Modal.Actions>
      </Modal>

      {/* Group Users Management Modal */}
      <Modal
        open={groupUsersModalOpen}
        onClose={closeGroupUsersModal}
        size="tiny"
        className="add-document-model addUserPopup"
      >
        <Modal.Content>
          <Header as="h3">Manage Group Users: {selectedGroup?.name}</Header>
          <div className="menu-item-search searchBox">
            <input
              type="text"
              id="groupUserSearch"
              value={userSearchTerm}
              onChange={(e) => setUserSearchTerm(e.target.value)}
              placeholder=""
              data-cy="search-group-user-input"
            />
            <label htmlFor="groupUserSearch">
              <img src="/images/search.svg" alt="Search" />
              Search
            </label>
          </div>
          {loadingGroupUsers ? (
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              minHeight: '200px',
              padding: '40px'
            }}>
              <div className="ui active inline loader" style={{ marginBottom: '16px' }}></div>
              <p style={{ margin: '0', fontSize: '16px', color: '#6b7280', textAlign: 'center' }}>
                Loading group members...
              </p>
            </div>
          ) : filteredUsers.length > 0 ? (
            <Paper sx={{ overflowX: "hidden" }} style={{ boxShadow: "none" }}>
              <MuiTableContainer style={{ border: "none", maxHeight: "400px", overflowY: "auto" }}>
                <MuiTable style={{ borderCollapse: "collapse", border: "none" }}>
                  <MuiTableHead>
                    <MuiTableRow>
                      <MuiTableCell style={{ borderBottom: "none" }}>
                        Name
                      </MuiTableCell>
                      <MuiTableCell style={{ borderBottom: "none" }}>

                      </MuiTableCell>
                      <MuiTableCell style={{ borderBottom: "none" }}>
                        Action
                      </MuiTableCell>
                    </MuiTableRow>
                  </MuiTableHead>
                  <MuiTableBody data-cy="group-users-list">
                    {filteredUsers.map((user) => {
                      const displayName = user.text.split(' (')[0];
                      const email = user.text.match(/\(([^)]+)\)/)?.[1] || '';

                      return (
                        <MuiTableRow key={user.key} onClick={() => handleUserToggle(user.value)}>
                          <MuiTableCell>
                            <div>
                              {displayName.length < 20 ? (
                                displayName
                              ) : (
                                <Popup
                                  content={displayName}
                                  inverted
                                  position="right center"
                                  style={{ borderRadius: "15px" }}
                                  trigger={
                                    <span>{displayName.slice(0, 20)}..</span>
                                  }
                                />
                              )}
                            </div>
                            <div className="usersPopupEmail">
                              {email.length < 30 ? (
                                email
                              ) : (
                                <Popup
                                  content={email}
                                  inverted
                                  position="right center"
                                  style={{ borderRadius: "15px" }}
                                  trigger={
                                    <span>{email.slice(0, 30)}..</span>
                                  }
                                />
                              )}
                            </div>
                          </MuiTableCell>
                          <MuiTableCell>
                            {/* Empty cell to match the structure */}
                          </MuiTableCell>
                          <MuiTableCell onClick={(e) => e.stopPropagation()}>
                            <input
                              value={user.value}
                              type="checkbox"
                              onChange={() => handleUserToggle(user.value)}
                              className="user-list-checkbox"
                              checked={selectedGroupUsers.includes(user.value)}
                              disabled={loadingGroupUsers}
                            />
                          </MuiTableCell>
                        </MuiTableRow>
                      );
                    })}
                  </MuiTableBody>
                </MuiTable>
              </MuiTableContainer>
            </Paper>
          ) : (
            <div className="emptyContainer">
              <Image src="/images/empty-state.svg" alt="Empty State" />
              <p className="emptyMessage">
                <span className="titleMessage">No data available</span>
              </p>
            </div>
          )}
          {selectedGroupUsers.length > 0 && (
            <div style={{
              marginTop: '16px',
              padding: '12px 16px',
              backgroundColor: '#f9fafb',
              borderRadius: '8px',
              borderLeft: '4px solid #0053ed'
            }}>
              <div style={{ fontSize: '14px', color: '#374151', fontWeight: '500' }}>
                {selectedGroupUsers.length} user{selectedGroupUsers.length > 1 ? 's' : ''} selected
              </div>
            </div>
          )}
        </Modal.Content>
        <Modal.Actions>
          <button
            className="cancel-btn"
            onClick={closeGroupUsersModal}
            data-cy="group-users-cancel"
          >
            Cancel
          </button>
          <button
            className="add-btn"
            onClick={handleSaveGroupUsers}
            data-cy="group-users-save"
            disabled={isUpdating || !hasGroupUsersChanges()}
          >
            {isUpdating ? 'Saving...' : 'Save'}
          </button>
        </Modal.Actions>
      </Modal>
    </div>
  );
};

export default GroupManagement;